import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Info } from "lucide-react";
import { Transaction } from "@/types/payments";
import { TransactionAccountLabel } from "@/components/cash/TransactionAccountLabel";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { isZeroAmountTransaction } from "@/utils/zeroAmountTransaction";
import { useTranslations } from "use-intl";
import { useFormatters } from "@financialharmony/ui";

interface TransactionDetailDialogProps {
  transaction: Transaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formatCurrency: (amount: number, currency: string) => string;
}

const BOOKING_STATUS_KEYS: Record<string, "pending" | "booked"> = {
  reserved: "pending",
  booked: "booked",
};

/**
 * Labeled field: renders nothing when `value` is null/undefined/empty so the detail view never
 * shows an empty row for a field this transaction happens not to have (issue #459 — most of
 * these columns are provider-dependent and routinely null).
 */
const Field = ({
  label,
  value,
  wide,
  mono,
}: {
  label: string;
  value?: string | null;
  wide?: boolean;
  mono?: boolean;
}) => {
  if (!value) return null;
  return (
    <div className={wide ? "col-span-2" : undefined}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={mono ? "font-mono text-sm" : "font-medium"}>{value}</p>
    </div>
  );
};

/**
 * Full-detail view for a single transaction (issue #459). `TransactionsTable` is a flat table
 * covering only 7 fields; this dialog surfaces every other bank_transactions column that reaches
 * the frontend — both the pre-existing hidden fields (remittance_information, counterparty_*)
 * and the ones added by issue #448 (structured_reference, purpose_code/text, creditor_*,
 * debtor_*). Fields that are null for a given transaction are simply omitted rather than
 * rendered empty. end_to_end_id is deliberately excluded (FIN-803).
 */
const TransactionDetailDialog: React.FC<TransactionDetailDialogProps> = ({
  transaction,
  open,
  onOpenChange,
  formatCurrency,
}) => {
  const t = useTranslations("consultantBank.transactionDetail");
  const { formatDate } = useFormatters();
  if (!transaction) return null;

  // Booking status (issue #363/#523) — 'reserved' rows are pending/unbooked; render as words,
  // not the raw enum value. Absent for legacy rows with no booking_status.
  const bookingStatusKey = BOOKING_STATUS_KEYS[transaction.booking_status ?? ""];
  const bookingStatusText = bookingStatusKey ? t(bookingStatusKey) : undefined;

  const isZeroAmount = isZeroAmountTransaction(transaction);
  const dateText = formatDate(transaction.date, "numeric");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{transaction.description || t("fallbackTitle")}</DialogTitle>
          <DialogDescription>
            {formatCurrency(transaction.amount, transaction.currency)} ·{" "}
            {dateText}
          </DialogDescription>
        </DialogHeader>

        {isZeroAmount && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>{t("zeroTitle")}</AlertTitle>
            <AlertDescription>
              {t("zeroBody")}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
          <div>
            <p className="text-xs text-muted-foreground">{t("labels.amount")}</p>
            <p className="font-medium">
              {formatCurrency(transaction.amount, transaction.currency)}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{t("labels.account")}</p>
            <div className="font-medium">
              <TransactionAccountLabel transaction={transaction} surface="dialog" />
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t("labels.date")}</p>
            <p className="font-medium">{dateText}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t("labels.type")}</p>
            {isZeroAmount ? (
              <p className="font-medium text-muted-foreground">–</p>
            ) : (
              <Badge variant={transaction.type === "credit" ? "default" : "outline"}>
                {transaction.type === "credit" ? t("credit") : t("debit")}
              </Badge>
            )}
          </div>
          <Field label={t("labels.bookingStatus")} value={bookingStatusText} />
          <Field label={t("labels.category")} value={isZeroAmount ? t("notApplicable") : transaction.category} />
          <Field label={t("labels.description")} value={transaction.description} wide />
          <Field
            label={t("labels.remittance")}
            value={transaction.remittanceInformation}
            wide
          />
          <Field
            label={t("labels.structuredReference")}
            value={transaction.structuredReference}
            wide
            mono
          />
          <Field label={t("labels.purposeCode")} value={transaction.purposeCode} mono />
          <Field label={t("labels.purposeText")} value={transaction.purposeText} />
          <Field label={t("labels.counterpartyName")} value={transaction.counterpartyName} wide />
          <Field label={t("labels.counterpartyAccount")} value={transaction.counterpartyAccount} mono />
          <Field label={t("labels.counterpartyIban")} value={transaction.counterpartyIban} mono />
          <Field label={t("labels.creditorName")} value={transaction.creditorName} wide />
          <Field label={t("labels.creditorAccount")} value={transaction.creditorAccount} mono />
          {/* issue #523 — IBAN is independent of the bban account field; each can be present or
              absent on its own, so this is a separate Field row, never merged into the one above. */}
          <Field label={t("labels.creditorIban")} value={transaction.creditorIban} mono />
          <Field label={t("labels.debtorName")} value={transaction.debtorName} wide />
          <Field label={t("labels.debtorAccount")} value={transaction.debtorAccount} mono />
          <Field label={t("labels.debtorIban")} value={transaction.debtorIban} mono />
        </div>

        {transaction.rawPayload !== undefined && transaction.rawPayload !== null && (
          <Collapsible className="mt-2">
            <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground [&[data-state=open]>svg]:rotate-180">
              <ChevronDown className="h-3 w-3 transition-transform" />
              {t("viewOriginal")}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs font-mono">
                {JSON.stringify(transaction.rawPayload, null, 2)}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionDetailDialog;
